/**
 * Copyright Highway9 Networks Inc.
 */
import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, type EntityState, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "..";
import { ApiTokenService } from "~/services";
import type { ApiToken } from "~/types/ApiToken";
import { createSocketPatchReducer } from "../events/socketPatch";

export const apiTokensAdapter = createEntityAdapter<ApiToken>();

interface ApiTokenState {
  tokensData: EntityState<ApiToken>;
  loading: boolean;
  selectedId: string | null;
  open: boolean;
}

const initialState: ApiTokenState = {
  tokensData: apiTokensAdapter.getInitialState(),
  loading: true,
  selectedId: null,
  open: false,
};

export const fetchApiTokens = createAsyncThunk(
  "apiToken/fetchApiTokens",
  async () => {
    try {
      const data = await ApiTokenService.getApiTokensData();
      return data;
    } catch (error) {
      console.error("Error fetching access config data:", error);
      throw error;
    }
  },
);

const apiTokenSlice = createSlice({
  name: "apiToken",
  initialState,
  reducers: {
    /** Store selection identity only; resolve the entity via selectors. */
    setSelected: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    setDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    applySocketPatches: createSocketPatchReducer(apiTokensAdapter, (s) => s.tokensData),
  },
  extraReducers: (builder) => {
    builder.addCase(fetchApiTokens.fulfilled, (state, action) => {
      state.loading = false;
      apiTokensAdapter.setAll(state.tokensData, action.payload);
    });
  },
});

export const apiTokenActions = apiTokenSlice.actions;

const apiTokenEntitySelectors = apiTokensAdapter.getSelectors(
  (state: RootState) => state.apiToken.tokensData,
);

export const apiTokenData = apiTokenEntitySelectors.selectAll;
export const apiTokenEntities = apiTokenEntitySelectors.selectEntities;

export const apiTokenState = createSelector(
  [(state: RootState) => state.apiToken.selectedId, apiTokenEntities],
  (selectedId, entities): ApiToken | null =>
    selectedId ? entities[selectedId] ?? null : null,
);

export const apiTokenLoading = (state: RootState) => state.apiToken.loading;
export const apiTokenOpen = (state: RootState) => state.apiToken.open;

export default apiTokenSlice.reducer;
